import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as rxjs from 'rxjs'

import * as dms from '../../../dms'
import * as organizations from '../../organizations'
import * as clusters from '../clusters'
import * as records from '../records'

import type { TypeParams } from './types'

/**
 * Fetches space records that can be added to the current cluster.
 * Always restricts results to the cluster's department via orgFilter, then
 * narrows further by any cascading space-dimension selections the user makes.
 * Signed off records are excluded, since they are locked.
 */
@ngCore.Injectable()
export class Api implements ec.models.Api<TypeParams> {
  /** @see {@link clusters.Collection} */
  private readonly _clustersEc = ngCore.inject(clusters.Collection)

  /** @see {@link dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(dms.schema.Feature)

  /** @inheritdoc */
  public list = ec.models.Api.list.using.everything<TypeParams>(
    (context, request) => {
      if (!context) {
        return rxjs.of([])
      }

      return rxjs
        .combineLatest({
          cluster:
            this._clustersEc.plugins.cachePlugin.feature.selectors.getCachedEntity(
              context.clusterId,
            ).asObservable,
          schema: this._schemaFeature.selectors.schema.asObservable,
        })
        .pipe(
          rxjs.first(),
          rxjs.switchMap(({ cluster, schema }) => {
            const leafOrgEntity = schema?.tryGetLeafEntityOf(
              organizations.constants.DIMENSION_ID,
            )

            const orgRecordId =
              cluster && leafOrgEntity
                ? cluster.organization[leafOrgEntity.id]
                : undefined

            const orgFilter:
              | ents.extra.space.Record.Filter.DimensionFilter
              | undefined =
              leafOrgEntity && orgRecordId
                ? { entityId: leafOrgEntity.id, recordIds: [orgRecordId] }
                : undefined

            const spaceFilter = request?.filter?.additionalFilters?.spaceFilter

            const filter = records.gql.inputs.filter.TRANSFORMER.getGqlFrom({
              ...(orgFilter && { orgFilter }),
              ...(spaceFilter && { spaceFilter }),
              surveyStatuses: ents.extra.space.SurveyStatus.ALL.filter(
                status => status !== ents.extra.space.SurveyStatus.SignedOff,
              ),
            })

            return records.gql.queries.spaceRecords.executor({
              ...context,
              filter,
              sort: request?.sort && {
                by: request.sort.by,
                descending: request.sort.descending,
              },
            })
          }),
        )
    },
  )
}
